import { useEffect } from 'react';
import { Link, Navigate, NavigateFunction, useMatch, useNavigate } from 'react-router-dom';

import { billingApi } from '@pukka/api';
import { clickstreamTracker } from '@pukka/clickstream';
import { hookUtils } from '@pukka/common/utils';
import { toast } from '@pukka/toast';
import { PageContainer, Text } from '@pukka/ui-components';
import { useQuery } from '@tanstack/react-query';

import { BillingState, PaymentStatus } from '../Billing.state';

function onRedirect(navigate: NavigateFunction) {
  navigate('/v2/settings/billing');
}

function RedirectPage() {
  const match = useMatch('v2/settings/billing/redirect/:type');
  const type = match?.params.type;
  const navigate = useNavigate();
  // Sample: /redirect?status=pending|success|failed -> PaymentStatus
  const { status, order_id: orderId, status_code: statusCode, transaction_status: transactionStatus } = hookUtils.useQueryParams();

  // This is for midtrans transaction
  useQuery({
    queryFn: () => billingApi.updatePaymentStatus({ order_id: orderId, status_code: statusCode, transaction_status: transactionStatus }),
    onSuccess(data) {
      const isSuccess = data.type === 'success';
      const toastFunc = isSuccess ? toast.success : toast.error;
      const eventName = isSuccess ? 'MOKAPOS_SETTING_BILLING_PAYMENT_SUCCESSFUL' : 'MOKAPOS_SETTING_BILLING_PAYMENT_FAILED';

      clickstreamTracker.trackBilling(eventName, {
        billing: {
          orderId,
        },
      });

      setTimeout(() => {
        toastFunc(data.message);
      }, 2000);

      navigate(`/v2${data.url}`);
    },
    enabled: !!orderId && !!statusCode && !!transactionStatus,
  });

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    // This is for recurring handler
    if (status) {
      BillingState.paymentStatus = status.toLowerCase() as PaymentStatus;

      const timeout = setTimeout(onRedirect.bind({}, navigate), 100);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [status, type, navigate]);

  if (status || (orderId && statusCode && transactionStatus)) {
    return (
      <PageContainer className="centering flex">
        <Text>
          Please wait while we redirecting you.{' '}
          <Link to="/v2/settings/billing" className="text-brand underline">
            Click here
          </Link>{' '}
          if you are not redirected automatically.
        </Text>
      </PageContainer>
    );
  }

  return <Navigate to="/v2/settings/billing" />;
}

export { RedirectPage, onRedirect as _onRedirect };
